import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ouropix/ui';
import { Button } from '@ouropix/ui';
import { Input } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Alert, AlertDescription } from '@ouropix/ui';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@ouropix/ui';
import {
  Globe, Plus, Trash2, ShieldCheck, AlertTriangle, Loader2, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

export const Route = createFileRoute('/_admin/seguranca/ip-allowlist')({
  component: IpAllowlistPage,
});

interface AllowlistResponse {
  allowed_ips: string[];
  enforced?: boolean;
}

/**
 * Valida CIDR básico: IPv4 x.x.x.x ou x.x.x.x/n. IPv6 também aceito no formato padrão.
 * Não valida octetos individuais (0-255) — deixa pro backend fazer validação canônica.
 */
function isValidCidr(input: string): boolean {
  const v = input.trim();
  // IPv4 com ou sem prefix
  if (/^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/.test(v)) {
    const parts = v.split('/');
    const octets = parts[0].split('.').map(Number);
    if (octets.some((o) => o > 255 || o < 0 || Number.isNaN(o))) return false;
    if (parts[1] !== undefined) {
      const prefix = Number(parts[1]);
      if (prefix < 0 || prefix > 32) return false;
    }
    return true;
  }
  // IPv6 — formato básico
  if (/^[0-9a-fA-F:]+(\/\d{1,3})?$/.test(v) && v.includes(':')) {
    if (v.includes('/')) {
      const prefix = Number(v.split('/')[1]);
      if (prefix < 0 || prefix > 128) return false;
    }
    return true;
  }
  return false;
}

function IpAllowlistPage() {
  const qc = useQueryClient();
  const [newCidr, setNewCidr] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery<AllowlistResponse>({
    queryKey: ['admin-ip-allowlist'],
    queryFn: async () => {
      const { data } = await api.get('/auth/admin/ip-allowlist');
      return {
        allowed_ips: Array.isArray(data?.allowed_ips) ? data.allowed_ips : [],
        enforced: Boolean(data?.enforced ?? (Array.isArray(data?.allowed_ips) && data.allowed_ips.length > 0)),
      };
    },
    retry: false,
  });

  const allowedIps = data?.allowed_ips ?? [];
  const enforced = Boolean(data?.enforced);

  const updateList = useMutation({
    mutationFn: (next: string[]) =>
      api.post('/auth/admin/ip-allowlist', { allowed_ips: next }),
    onSuccess: () => {
      toast.success('Allowlist atualizada');
      qc.invalidateQueries({ queryKey: ['admin-ip-allowlist'] });
      setNewCidr('');
      setInputError(null);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Falha ao atualizar allowlist');
    },
  });

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const v = newCidr.trim();
    if (!v) {
      setInputError('Digite um CIDR');
      return;
    }
    if (!isValidCidr(v)) {
      setInputError('Formato inválido. Use x.x.x.x ou x.x.x.x/24');
      return;
    }
    if (allowedIps.includes(v)) {
      setInputError('Este CIDR já está na lista');
      return;
    }
    setInputError(null);
    updateList.mutate([...allowedIps, v]);
  };

  const handleRemove = (cidr: string) => {
    updateList.mutate(allowedIps.filter((c) => c !== cidr));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Globe className="h-7 w-7" />
          Restrição de IP
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Restrinja o login da sua conta admin a IPs ou blocos CIDR específicos. Lista vazia = aceita qualquer IP.
        </p>
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertDescription>
            Não foi possível carregar allowlist:{' '}
            {(error as any)?.response?.status === 404
              ? 'endpoint ainda não disponível.'
              : (error as Error)?.message ?? 'erro desconhecido'}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className={`h-4 w-4 ${enforced ? 'text-green-400' : 'text-muted-foreground'}`} />
              <span className="text-xs text-muted-foreground">Status</span>
            </div>
            <p className="text-lg font-bold mt-1">
              {enforced ? 'Ativa' : 'Inativa'}
            </p>
            <p className="text-xs text-muted-foreground">
              {enforced
                ? 'Apenas IPs listados podem fazer login.'
                : 'Sem restrição de IP.'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-blue-400" />
              <span className="text-xs text-muted-foreground">CIDRs permitidos</span>
            </div>
            <p className="text-lg font-bold mt-1">{allowedIps.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-400" />
              <span className="text-xs text-muted-foreground">Atenção</span>
            </div>
            <p className="text-xs mt-1 text-muted-foreground">
              Adicione seu IP antes de ativar — caso contrário você pode se bloquear.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Adicionar IP ou bloco CIDR</CardTitle>
          <CardDescription>
            Exemplos: <code className="px-1 py-0.5 bg-muted rounded text-xs">192.168.1.1</code>,{' '}
            <code className="px-1 py-0.5 bg-muted rounded text-xs">200.150.0.0/16</code>,{' '}
            <code className="px-1 py-0.5 bg-muted rounded text-xs">2001:db8::/32</code>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAdd} className="flex gap-2">
            <div className="flex-1">
              <Input
                type="text"
                value={newCidr}
                onChange={(e) => {
                  setNewCidr(e.target.value);
                  if (inputError) setInputError(null);
                }}
                placeholder="192.168.1.0/24"
                disabled={updateList.isPending}
                className="font-mono"
                aria-invalid={!!inputError}
              />
              {inputError && (
                <p className="text-xs text-destructive mt-1">{inputError}</p>
              )}
            </div>
            <Button type="submit" disabled={updateList.isPending || !newCidr.trim()}>
              {updateList.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">CIDRs atuais</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : allowedIps.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 text-sm text-muted-foreground">
              <Info className="h-6 w-6" />
              Nenhum CIDR cadastrado. Login aceita qualquer IP.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>CIDR</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="w-24 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allowedIps.map((cidr) => (
                  <TableRow key={cidr}>
                    <TableCell>
                      <code className="font-mono text-sm select-all">{cidr}</code>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {cidr.includes(':') ? 'IPv6' : cidr.includes('/') ? 'Bloco IPv4' : 'IPv4'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(cidr)}
                        disabled={updateList.isPending}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
